/*
 * <vt-toc>: "On this page", a table of contents built from the headings of a page's or a policy's text.
 *
 * Markup contract (see snippets/vitto-toc.liquid):
 *   <div class="vt-toc-layout">
 *     <vt-toc data-content="selector of the text" hidden> <details> <summary>…</summary> <nav><ol data-toc-list></ol></nav> </details> </vt-toc>
 *     <div class="vt-toc-layout__content">…the text…</div>
 *   </div>
 *
 * The text's top heading level (h2, else h3, else h4) and the level below it are listed, and each heading gets an id
 * (its own if it has one) so it can be linked. With fewer than two headings the list stays hidden and the page keeps
 * one column; otherwise the layout is marked "on" for its two columns. A click scrolls smoothly to the heading
 * (instantly with reduced motion), puts it in the address bar and moves focus there; while reading, the section at
 * the top of the screen is marked. Open beside the text on desktop, folded above it on phones.
 */

/** "  Contact  us! " -> "contact-us". */
function tocSlug(text) {
  return String(text)
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** The headings to list: the text's top level among h2–h4, and the level below it. */
function tocHeadings(content) {
  for (const level of [2, 3, 4]) {
    if (content.querySelector(`h${level}`)) return [...content.querySelectorAll(`h${level}, h${level + 1}`)];
  }
  return [];
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = { tocSlug, tocHeadings };
}

if (typeof customElements !== 'undefined' && !customElements.get('vt-toc')) {
  customElements.define(
    'vt-toc',
    class Toc extends HTMLElement {
      constructor() {
        super();
        this.onClick = this.onClick.bind(this);
        this.markCurrent = this.markCurrent.bind(this);
        this.onScroll = this.onScroll.bind(this);
      }

      connectedCallback() {
        const content = document.querySelector(this.dataset.content);
        this.headings = content ? tocHeadings(content) : [];
        if (this.headings.length < 2) return;

        const list = this.querySelector('[data-toc-list]');
        const topLevel = this.headings[0].tagName;
        const taken = new Set([...document.querySelectorAll('[id]')].map((el) => el.id));
        list.replaceChildren(
          ...this.headings.map((heading) => {
            if (!heading.id) heading.id = this.uniqueId(tocSlug(heading.textContent) || 'section', taken);
            const item = document.createElement('li');
            item.className = heading.tagName === topLevel ? 'vt-toc__item' : 'vt-toc__item vt-toc__item--sub';
            const link = document.createElement('a');
            link.className = 'vt-toc__link';
            link.href = `#${heading.id}`;
            link.textContent = heading.textContent.replace(/\s+/g, ' ').trim();
            item.append(link);
            return item;
          })
        );
        this.links = [...list.querySelectorAll('a')];

        this.hidden = false;
        this.closest('.vt-toc-layout')?.classList.add('vt-toc-layout--on');
        // Open beside the text on desktop (and kept open: its summary is just a heading there), folded on phones.
        const details = this.querySelector('details');
        this.desktop = window.matchMedia('(min-width: 990px)');
        details.open = this.desktop.matches;
        details.addEventListener('toggle', () => {
          if (this.desktop.matches && !details.open) details.open = true;
        });
        this.desktop.addEventListener('change', () => (details.open = this.desktop.matches));
        this.addEventListener('click', this.onClick);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.markCurrent();

        // The ids did not exist when the browser looked for the address's #section, so go there now.
        const target = window.location.hash && document.getElementById(decodeURIComponent(window.location.hash.slice(1)));
        if (target && this.headings.includes(target)) target.scrollIntoView({ behavior: 'auto', block: 'start' });
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
      }

      uniqueId(base, taken) {
        let id = base;
        for (let n = 2; taken.has(id); n++) id = `${base}-${n}`;
        taken.add(id);
        return id;
      }

      onClick(event) {
        const link = event.target.closest('a[href^="#"]');
        if (!link) return;
        const heading = document.getElementById(decodeURIComponent(link.hash.slice(1)));
        if (!heading) return;
        event.preventDefault();
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        heading.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
        history.pushState(null, '', link.hash);
        heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
        this.setCurrent(link);
      }

      onScroll() {
        if (this.ticking) return;
        this.ticking = true;
        window.requestAnimationFrame(() => {
          this.ticking = false;
          this.markCurrent();
        });
      }

      /**
       * The current section is the last heading that has passed a line a quarter of the way down the screen (below
       * the header); at the bottom of the page, the last section, whose heading may never get that high.
       */
      markCurrent() {
        const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
        const line = header + window.innerHeight * 0.25;
        const page = document.documentElement;
        const atBottom = page.scrollHeight > window.innerHeight && window.scrollY + window.innerHeight >= page.scrollHeight - 2;
        let current = 0;
        this.headings.forEach((heading, i) => {
          if (heading.getBoundingClientRect().top <= line) current = i;
        });
        this.setCurrent(this.links[atBottom ? this.links.length - 1 : current]);
      }

      setCurrent(link) {
        this.links.forEach((other) => other.toggleAttribute('aria-current', false));
        link.setAttribute('aria-current', 'location');
      }
    },
  );
}
